import React, { useRef } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

const CsAndItTable8 = ({ data }) => {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  const years = data.map((item) => item.year);
  const employment_14_18 = data.map((item) => Number(item.age_14_18));
  const employment_22_24 = data.map((item) => Number(item.age_22_24));
  const yoyChange_14_18 = data.map((item) =>
    item.yoy_change_14_18 ? Number(item.yoy_change_14_18) : null,
  );
  const yoyChange_22_24 = data.map((item) =>
    item.yoy_change_22_24 ? Number(item.yoy_change_22_24) : null,
  );

  // Total Summary Calculation
  const firstYearTotal = employment_14_18[0] + employment_22_24[0];
  const lastYearTotal =
    employment_14_18[employment_14_18.length - 1] +
    employment_22_24[employment_22_24.length - 1];

  const growthPercent = (
    ((lastYearTotal - firstYearTotal) / firstYearTotal) *
    100
  ).toFixed(1);

  const peakYear = data.reduce(
    (max, row) => {
      const total = Number(row.age_14_18) + Number(row.age_22_24);
      return total > max.total ? { year: row.year, total } : max;
    },
    { year: data[0].year, total: firstYearTotal },
  );

  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Employment (14–18)",
        data: employment_14_18,
        backgroundColor: "#FFA1B5",
        borderRadius: 6,
        type: "bar",
      },
      {
        label: "Employment (22–24)",
        data: employment_22_24,
        backgroundColor: "#FFE29A",
        borderRadius: 6,
        type: "bar",
      },
      {
        label: "YoY Change (%) (14–18)",
        data: yoyChange_14_18,
        borderColor: "#FFA1B5",
        backgroundColor: "#FFA1B5",
        borderWidth: 2,
        pointRadius: 5,
        tension: 0.3,
        type: "line",
        yAxisID: "y2",
      },
      {
        label: "YoY Change (%) (22–24)",
        data: yoyChange_22_24,
        borderColor: "#FFE29A",
        backgroundColor: "#FFE29A",
        borderWidth: 2,
        pointRadius: 5,
        tension: 0.3,
        type: "line",
        yAxisID: "y2",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment & YoY Change (%) by Age Group (2018-2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 14, weight: "bold" },
        },
      },
      y2: {
        position: "right",
        ticks: { callback: (value) => `${value}%` },
        grid: { drawOnChartArea: false },
        title: {
          display: true,
          text: "YoY (%)",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        grid: { display: false },
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

 const downloadPDF = () => {
   const input = pdfRef.current;
   html2canvas(input, {
     scale: 2,
     useCORS: true,
     backgroundColor: "#ffffff",
     scrollY: -window.scrollY,
   }).then((canvas) => {
     const imgData = canvas.toDataURL("image/png");
     const pdf = new jsPDF("p", "mm", "a4");
     const pageWidth = pdf.internal.pageSize.getWidth();
     const pageHeight = pdf.internal.pageSize.getHeight();
     const imgWidth = pageWidth - 10;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;

     let heightLeft = imgHeight;
     let position = 0;
     let pageNumber = 1;

     const addHeader = () => {
       const logoWidth = 20; // ideal header width
       const logoHeight = 20; // adjust ratio if needed

       pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
       pdf.setFontSize(26);
       pdf.text("UTahData.org", 95, 22);
       pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
       pdf.line(10, 35, 200, 35);
     };

     const addFooter = (pageNumber) => {
       pdf.setFontSize(9);
       const footerY = pageHeight - 10;
       pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         10,
         footerY,
       );
       pdf.text(
         `Page ${pageNumber}`,
         pageWidth - 10,
         footerY,
         null,
         null,
         "right",
       );
     };

     // First page
     addHeader();
     pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
     heightLeft -= pageHeight - 32;

     // Add more pages if needed
     while (heightLeft > 0) {
       pdf.addPage();
       pageNumber++;
       addHeader();
       position = heightLeft - imgHeight;
       pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;
       addFooter(pageNumber);
     }

     // Final footer
     // ===== FINAL FOOTER SAFE AREA =====

     const footerTopY = pageHeight - 40; // start footer 40mm from bottom
     const footerContentY = pageHeight - 28; // logos row
     const footerBottomTextY = pageHeight - 10; // bottom text row

     // Top Divider Line
     pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

     // UtahData logo
     const utahWidth = 18;
     const utahHeight = 18;
     pdf.addImage(
       utahData,
       "PNG",
       20,
       footerContentY - 8,
       utahWidth,
       utahHeight,
     );

     // Built by
     pdf.setFontSize(10);
     pdf.text("Built by", 45, footerContentY);

     // BODEX logo
     const bodexWidth = 36;
     const bodexHeight = bodexWidth * 0.3;
     pdf.addImage(
       bodex,
       "PNG",
       60,
       footerContentY - 6,
       bodexWidth,
       bodexHeight,
     );

     // Using text
     pdf.text("using", 102, footerContentY);

     // KPI logo
     pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

     // Version
     pdf.text("20250916.v3.198", 140, footerContentY);

     // Bottom Info Line
     pdf.setFontSize(8);
     pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
     pdf.text(
       `Downloaded on: ${new Date().toLocaleDateString()}`,
       pageWidth - 10,
       footerBottomTextY,
       null,
       null,
       "right",
     );

     pdf.save("cs_it_emp_yoy_age_group.pdf");
   });
 };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-lg border border-gray-200"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment & YoY Trends by Age (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm">Overall Growth</p>
            <p className="text-xl font-bold text-blue-700">+{growthPercent}%</p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm">Peak Year</p>
            <p className="text-xl font-bold text-yellow-700">{peakYear.year}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm">2024 Total Employment</p>
            <p className="text-xl font-bold text-green-700">
              {lastYearTotal.toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Age (14–18)
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Age (22–24)
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => {
                    const total = Number(row.age_14_18) + Number(row.age_22_24);
                    return (
                      <tr
                        key={row.year}
                        className={`${
                          row.year === 2024
                            ? "bg-green-100 font-bold"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        <td className="p-2 text-left">{row.year}</td>
                        <td
                          className="p-2"
                          style={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {Number(row.age_14_18).toLocaleString("en-US")}
                        </td>
                        <td
                          className="p-2"
                          style={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {Number(row.age_22_24).toLocaleString("en-US")}
                        </td>
                        <td
                          className="p-2"
                          style={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {total.toLocaleString("en-US")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg"
        >
          Download as PDF
          <img src={pdfIcon} alt="PDF icon" className="w-6" />
        </button>
      </div>
    </>
  );
};

export default CsAndItTable8;
